import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'
import { parseProducerImageUrl } from 'utils/producer.ts'
import styles from 'features/contracts/done/Done.module.scss'
import { DoneProps } from 'features/contracts/add/types.ts'
import { Button, Card } from '@boltenergy-be/design-system'
import { routes } from 'types/routes.ts'
import { UserTypes } from 'store/auth/types.ts'
import { setMoveFlow } from 'store/app/slice.ts'
import { useStoreSelector } from 'hooks/store.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import parse from 'html-react-parser'
import StartCard from 'features/contracts/done/components/start-card/StartCard.tsx'
import StopCard from 'features/contracts/done/components/stop-card/StopCard.tsx'
import { useLocation } from 'react-router'
import classNames from 'classnames'
import Link from 'components/link/Link.tsx'
import { MOVE_FLOWS, MoveFlow } from '@boltenergy-be/shared'

const Done = ({ producer, onExit = () => {} }: DoneProps) => {
  // REDUX STORE
  const { move } = useStoreSelector((store) => store.app)
  const { userType } = useStoreSelector((store) => store.auth)
  const dispatch = useDispatch()

  // i18n
  const { t } = useTranslation('contracts')
  const lowerCaseLanguage = useGetLanguage('lowercase')

  // React router
  const navigate = useNavigate()
  const { pathname } = useLocation()

  // Constants
  const isSuperUser = userType === UserTypes.SUPER_USER
  const isMove = pathname === routes.CONTRACTS_MOVE

  /**
   * Handles back button click
   */
  const handleBack = () => {
    void navigate(routes.MOVE)
  }

  useEffect(() => {
    if (pathname === routes.CONTRACTS_TERMINATE) {
      if (move.flow === MOVE_FLOWS.start) {
        dispatch(setMoveFlow(move.started ? MOVE_FLOWS.startStop : MOVE_FLOWS.stop))
      }
    } else {
      if (isMove && move.flow === MOVE_FLOWS.start) {
        dispatch(setMoveFlow(move.stopped ? MOVE_FLOWS.stopStart : MOVE_FLOWS.startStop))
      }
    }
  }, []) // oxlint-disable-line react-hooks/exhaustive-deps

  return (
    <section
      className={classNames(styles.done, {
        [styles['two-columns']]: ([MOVE_FLOWS.startStop, MOVE_FLOWS.stopStart] as MoveFlow[]).includes(move.flow)
      })}
    >
      {/* START - PRODUCER CARD (ALWAYS FOR SALES) */}
      {producer && move.flow === MOVE_FLOWS.start && (
        <Card className={styles['producer-card']}>
          <div className={styles.content}>
            <Card.Title variant="h5">
              {parse(
                t(($) => $.done.producer[isSuperUser ? 'isSales' : 'isCustomer'].title, {
                  defaultValue: '',
                  producer: producer.name[lowerCaseLanguage]
                })
              )}
            </Card.Title>
            <p>{t(($) => $.done.producer[isSuperUser ? 'isSales' : 'isCustomer'].description)}</p>
            <Button onClick={handleBack} className={styles.button}>
              {t(($) => $.done.producer[isSuperUser ? 'isSales' : 'isCustomer'].button)}
            </Button>
          </div>
          <img
            src={parseProducerImageUrl(producer.images?.profile?.url, 600)}
            alt={producer.name[lowerCaseLanguage]}
            width={600}
            height={360}
          />
        </Card>
      )}

      {/* START_STOP */}
      {move.flow === MOVE_FLOWS.startStop && (
        <>
          {/* NEW ADDRESS CARD */}
          <StartCard {...{ handleBack }} />

          {/* TERMINATE CARD */}
          <StopCard {...{ handleBack }} />
        </>
      )}

      {/* STOP_START */}
      {move.flow === MOVE_FLOWS.stopStart && (
        <>
          {/* TERMINATE CARD */}
          <StopCard {...{ handleBack }} />

          {/* NEW ADDRESS CARD */}
          <StartCard {...{ handleBack }} />
        </>
      )}

      {/* STOP_START/START_STOP - ALL SET CARD */}
      {([MOVE_FLOWS.startStop, MOVE_FLOWS.stopStart] as MoveFlow[]).includes(move.flow) && move.started && move.stopped && (
        <Link href={routes.CONTRACT} representation="button" className="mx-auto grid-col-full" onNavigate={onExit}>
          {t(($) => $.done.allSet.action)}
        </Link>
      )}

      {/* STOP - TERMINATE CARD */}
      {move.flow === MOVE_FLOWS.stop && <StopCard {...{ handleBack }} />}
    </section>
  )
}

export default Done
